

export function memoize<T extends (...args: any) => any>(fn: T, key?: (...args: Parameters<T>) => any): T & {
    delete: (...args: Parameters<T>) => void
} {
    const cache = new Map();
    // @ts-ignore
    return Object.assign((...args) => {
        // @ts-ignore
        const k = key ? key(...args) : args[0];
        if (cache.has(k)) return cache.get(k);
        const r = fn(...args);
        cache.set(k, r);
        return r;
    }, {
        delete(...args) {
            // @ts-ignore
            const k = key ? key(...args) : args[0];
            cache.delete(k);
        }
    });
};

// eslint-disable-next-line no-restricted-globals
let debugSleepScale = (typeof location !== 'undefined' && +new URLSearchParams(location?.search).get('debug-sleep-scale')) || 1;
export const setDebugSleepScale = value => {
    debugSleepScale = value;
};
export const getDebugSleepScale = () => debugSleepScale;
export const sleep = (ms) => new Promise(resolve => setTimeout(resolve, debugSleepScale * ms));

export function loadImage(url: string): Promise<HTMLImageElement> {
    /** @type {Promise<HTMLImageElement>} */
    return (new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => {
            reject(new Error(`Failed to load image ${url}`));
        };
        img.crossOrigin = 'anonymous';
        img.src = url;
    }));
}

export const loadImageMemo = memoize(loadImage, url => url);

export const loadScript = memoize((url: string) => new Promise<void>(r => {
    const script = document.createElement('script');
    script.src = url;
    script.onload = () => r();
    document.head.appendChild(script);
}), url => url);

/**
 * @param {number} w 
 * @param {number} h 
 */
export const createTempCanvas = (w, h) => {
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    return canvas;
};

/**
 * @param {HTMLCanvasElement} canvas 
 */
export const cloneCanvas = (canvas) => {
    const newCanvas = createTempCanvas(canvas.width, canvas.height);
    const ctx = newCanvas.getContext('2d');
    ctx.drawImage(canvas, 0, 0);
    return newCanvas;
};

/**
 * 
 * @param {string} str 
 * @param {RegExp} regex 
 * @param {(substring: string, ...args: any[]) => Promise<string>} asyncFn 
 * @returns 
 */
export async function replaceAsync(str, regex, asyncFn) {
    const promises = [];
    str.replace(regex, (match, ...args) => {
        const promise = asyncFn(match, ...args);
        promises.push(promise);
        return match;
    });
    const data = await Promise.all(promises);
    return str.replace(regex, () => data.shift());
}

export function dataURItoArrayBuffer(/** @type {string} */dataURI) {
    // convert base64 to raw binary data held in a string
    // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
    const byteString = atobFix(dataURI.split(',')[1]);
  
    // write the bytes of the string to an ArrayBuffer
    const ab = new ArrayBuffer(byteString.length);
  
    // create a view into the buffer
    const ia = new Uint8Array(ab);
  
    // set the bytes of the buffer to the correct values
    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }
    return ab;
}

/**
 * @param {{
 *   gifParse: any,
 *   promiseConcurrency: import('promise-concurrency')['default'],
 * }} param0 
 */
export const createFramesGenerator = ({ gifParse, promiseConcurrency }) => {
    
    const gifParseMemo = memoize((/** @type {string} */gifDataUrl) => {
        return gifParse(dataURItoArrayBuffer(gifDataUrl));
    }, gifDataUrl => gifDataUrl);
    /**
     * @param {string} initSvgCode 
     */
    const createGetImageAtTime = (initSvgCode) => memoize(async (/** @type {number} */time) => {
        // console.log('time', time);
        // console.time('getImageAtTime' + time);
        const svgCode = await replaceAsync(initSvgCode, /data:image\/gif;base64,[^"']+/g, async (gifDataUrl) => {
            const { frames, duration } = await gifParseMemo(gifDataUrl);
            // console.log('gif duration', duration, frames, gifDataUrl.split(',')[1].slice(0, 10));
            const timeIn = (time % duration + duration) % duration;
            let frameTime = 0;
            // console.log('timeIn', timeIn);
            for (const frame of frames) {
                if (timeIn < frameTime + frame.delay) {
                    // console.log('took', frames.indexOf(frame));
                    return frame.canvas.toDataURL();
                }
                frameTime += frame.delay;
            }
            return frames[0].canvas.toDataURL();
        });
        // console.timeEnd('getImageAtTime' + time);
        // console.time('loadImage');
        const svgUrl = `data:image/svg+xml;base64,${btoaFix(svgCode)}`;
        const image = await loadImage(svgUrl);
        // console.timeEnd('loadImage');
        return image;
    }, (time) => time + '');
    /**
     * @param {{
     *   front: string,
     *   back: string,
     *   angles: { angle: number, time: number }[],
     *   width: number,
     *   height: number,
     *   isHorizontal?: boolean,
     *   backgroundColor?: string,
     * }} param0 
     */
    async function generateFlipCardFrames({ front, back, angles, width, height, isHorizontal = false, backgroundColor = '' }) {

        // console.log('angles', angles);

        const frontCode = atobFix(front.split(',').pop());
        const backCode = atobFix(back.split(',').pop());

        {
            const svgSize = getSvgSize(frontCode);
            if (!width && !height) {
                width = svgSize.width;
                height = svgSize.height;
            }
            if (!width) width = height * svgSize.width / svgSize.height;
            if (!height) height = width * svgSize.height / svgSize.width;
        }
        
        const getImageAtTimeFront = createGetImageAtTime(frontCode);
        const getImageAtTimeBack = createGetImageAtTime(backCode);

        let debugFrames = false;
        let debugCtx = debugFrames ? createTempCanvas(width, height).getContext('2d') : null;
        let debugCtxFrames = [];

        /** @type {CanvasRenderingContext2D[]} */
        const frames = [];
        const promisesFns = angles.map((angle, i) => {
            return async () => {
                // console.log('angle.time', angle.time);
                if (process.env.NODE_ENV === 'development') console.time('frame' + i);
                const cosAngle = Math.cos(Math.PI * angle.angle / 180);
                const canvas = createTempCanvas(width, height);
                const ctx = canvas.getContext('2d');
                if (backgroundColor) {
                    ctx.fillStyle = backgroundColor;
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                }
                /** @type {HTMLImageElement} */
                let img;
                /** @type {number} */
                let w;
                /** @type {number} */
                let h;
                if (cosAngle !== 0) {
                    if (isHorizontal) {
                        const rotatedHeight = height * Math.cos(Math.PI * angle.angle / 180);
                        img = rotatedHeight > 0
                            ? await getImageAtTimeFront(angle.time)
                            : await getImageAtTimeBack(angle.time);
                        // tmpCtx.clearRect(0, 0, tmpCanvas.width, tmpCanvas.height);
                        // tmpCtx.drawImage(img, 0, 0, width, height);
                        h = Math.abs(rotatedHeight);
                        w = width;
                    } else {
                        const rotatedWidth = width * Math.cos(Math.PI * angle.angle / 180);
                        img = rotatedWidth > 0
                            ? await getImageAtTimeFront(angle.time)
                            : await getImageAtTimeBack(angle.time);
                        // tmpCtx.clearRect(0, 0, tmpCanvas.width, tmpCanvas.height);
                        // tmpCtx.drawImage(img, 0, 0, width, height);
                        w = Math.abs(rotatedWidth);
                        h = height;
                    }
                    // console.time('drawImage' + i);
                    // draw at center
                    const tmpCtx = createTempCanvas(width, height).getContext('2d');
                    // tmpCtx.imageSmoothingEnabled = false;
                    tmpCtx.drawImage(img, 0, 0, width, height);
                    // ctx.imageSmoothingEnabled = false;
                    ctx.drawImage(tmpCtx.canvas, width / 2 - w / 2, height / 2 - h / 2, w, h);
                    // console.timeEnd('drawImage' + i);
                    // console.log(img.src.slice(0, 100));
                    // document.body.append(img);
                }
                // console.timeEnd('toDataURL' + i);
                if (process.env.NODE_ENV === 'development') console.timeEnd('frame' + i);
                frames[i] = ctx;
                sleep(16);
                // debug
                // document.body.append(ctx.canvas);
                if (debugFrames) {
                    debugCtxFrames[i] = ctx.canvas;
                }
            };
        });
        // console.time('promiseConcurrency');
        await promiseConcurrency(promisesFns, 4);
        // console.timeEnd('promiseConcurrency');
        if (debugCtx) {
            let frameIndex = 0;
            let paused = false;
            debugCtxFrames = debugCtxFrames.slice(75, 75 + 2);
            debugCtx.canvas.style.clipPath = 'inset(428px 1783px 1900px 273px)';
            debugCtx.canvas.style.imageRendering = 'pixelated';
            setInterval(() => {
                if (paused) return;
                debugCtx.clearRect(0, 0, width, height);
                debugCtx.imageSmoothingEnabled = false;
                debugCtx.drawImage(debugCtxFrames[frameIndex], 0, 0);
                // console.log(frameIndex);
                frameIndex = (frameIndex + 1) % debugCtxFrames.length;
            }, 100);
            debugCtx.canvas.addEventListener('mouseenter', e => {
                paused = true;
            });
            debugCtx.canvas.addEventListener('mouseleave', e => {
                paused = false;
            });
            document.body.append(debugCtx.canvas);
        }

        return frames;
    }

    /**
     * 
     * @param {Parameters<typeof generateFlipCardFrames>[0] & { type: string, quality: number }} options 
     */
    const generateFlipCardFramesAsDataUrl = async (options) => {
        // console.time('generateFlipCardFramesCtx');
        const frames = await generateFlipCardFrames(options);
        // console.timeEnd('generateFlipCardFramesCtx');
        // console.time('framesDataUrls');
        const framesDataUrls = frames.map(ctx => ctx.canvas.toDataURL(options.type, options.quality));
        // console.timeEnd('framesDataUrls');
        return framesDataUrls;
    };

    return { asCtx: generateFlipCardFrames, asDataUrl: generateFlipCardFramesAsDataUrl };

};

export function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result + '');
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

/**
 * @see https://stackoverflow.com/questions/30106476/using-javascripts-atob-to-decode-base64-doesnt-properly-decode-utf-8-strings
 * @param {string} str 
 */
export function btoaFix(str) {
    return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => {
        return String.fromCharCode(parseInt(p1, 16));
    }));
}

/**
 * @param {string} str 
 */
export function atobFix(str) {
    return decodeURIComponent(Array.prototype.map.call(atob(str), c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
}

/**
 * @param {string} url 
 */
export const fetchAsDataUrl = async (url) => {
    const res = await fetch(url);
    const blob = await res.blob();
    return blobToDataUrl(blob);
};


/**
 * @param {string} svgCode 
 */
export const getSvgSize = svgCode => {
    const svg = typeof DOMParser !== 'undefined'
        ? new DOMParser().parseFromString(svgCode, 'image/svg+xml').documentElement
        : (() => {
            const svgAttrs = svgCode.match(/<svg([^>]*)>/)[1];
            return {
                getAttribute(name) {
                    // /width=(["'])(.*?)\1/
                    const match = svgAttrs.match(new RegExp(`${name}=(["'])(.*?)\\1`));
                    return match ? match[2] : null;
                },
            };
        })();
    const widthAttr = +svg.getAttribute('width') || 0;
    const heightAttr = +svg.getAttribute('height') || 0;
    if (widthAttr && heightAttr) {
        return {
            width: widthAttr,
            height: heightAttr
        };
    }
    const viewBoxAttr = svg.getAttribute('viewBox');
    if (viewBoxAttr) {
        const viewBoxWidth = +viewBoxAttr.replace(/^\d+\s\d+\s(\d+\.?[\d])\s(\d+\.?[\d])/, "$1");
        const viewBoxHeight = +viewBoxAttr.replace(/^\d+\s\d+\s(\d+\.?[\d])\s(\d+\.?[\d])/, "$2");
        if (widthAttr) {
            return {
                width: widthAttr,
                height: widthAttr * viewBoxHeight / viewBoxWidth
            };
        } else if (heightAttr) {
            return {
                width: heightAttr * viewBoxWidth / viewBoxHeight,
                height: heightAttr
            };
        }
        return {
            width: viewBoxWidth,
            height: viewBoxHeight
        };
    }
    return {
        width: widthAttr,
        height: heightAttr
    };
};


/**
 * @param {string | Blob} svgBlobOrDataUrl 
 * @param {{ width?: number, height?: number, randomPixels?: number, randomPixelsDepth?: number }} param1 
 */
export async function svgBlobToPngDataUrl(svgBlobOrDataUrl, { width = undefined, height = undefined } = {}) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const url = typeof svgBlobOrDataUrl === 'string' ? svgBlobOrDataUrl :  await blobToDataUrl(svgBlobOrDataUrl);

    const img = await loadImage(url);

    if (!width && !height) {
        width = img.width;
        height = img.height;
    }
    if (!width) width = height * img.width / img.height;
    if (!height) height = width * img.height / img.width;

    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const pngDataUrl = canvas.toDataURL();

    return pngDataUrl;
}

/**
 * @param {string} svgCode 
 * @param {Parameters<svgBlobToPngDataUrl>[1]} options
 */
export async function svgCodeToPngDataUrl(svgCode, options) {
    return svgBlobToPngDataUrl(new Blob([svgCode], { type: 'image/svg+xml' }), options);
}

export const svgToPng = async (svgCode: string, { width, height, scale = 1 }: { width?: number; height?: number; scale?: number; }) => {
    const params = {
        width,
        height,
    };
    if (!width || !height) {
        const size = getSvgSize(svgCode);
        if (width && !height) {
            params.height = width / size.width * size.height;
        }
        if (!width && height) {
            params.width = height / size.height * size.width;
        }
        if (!width && !height) {
            params.width = size.width;
            params.height = size.height;
        }
    }
    params.width *= scale;
    params.height *= scale;
    const pngDataUrl = await svgCodeToPngDataUrl(svgCode, params);
    return pngDataUrl;
};

/**
 * 
 * @param {number} fps 
 * @param {(x: number, type: string) => number[]} Easing 
 */
export function getAngles(fps, Easing) {
    let t = 0, dt = 1 / fps * 1000;
    /** @type {{ angle: number, time: number }[]} */
    const angles = [];
    const push = (/** @type {number} */v) => {
        const prevAngle = angles.length ? angles[angles.length - 1].angle : 0;
        const newAngle = v * 180;
        if (angles.length && Math.cos(Math.PI * prevAngle / 180) * Math.cos(Math.PI * newAngle / 180) <= 0) {
            t = 0;
        }
        angles.push({ angle: newAngle, time: t });
        t += dt;
    };
    new Array(Math.ceil(0.5 * fps)).fill(0).forEach(push);
    // console.log([...angles]);
    Easing(Math.ceil(1.5 * fps), 'quadratic').forEach(push);
    // console.log([...angles]);
    new Array(Math.ceil(0.5 * fps)).fill(1).forEach(push);
    // console.log([...angles]);
    Easing(Math.ceil(1.5 * fps), 'quadratic').map(v => 1 + v).forEach(push);
    // console.log([...angles]);
    for (let i = angles.length - 1; i >= 0; i--) {
        if (angles[i].time === 0) {
            while (i < angles.length) {
                angles[i].time -= angles[angles.length - 1].time;
                i++;
            }
            break;
        }
    }
    // debug
    // angles.forEach(o => o.angle = 0);
    return angles;
}

/**
 * 
 * @param {{
 *   parseGIF: any,
 *   decompressFrames: any,
 * }} param0 
 * @returns 
 */
export const createGifParse = ({ parseGIF, decompressFrames }) => {
    
    /**
     * @param {ArrayBuffer} arrayBuffer
     */
    async function gifParse(arrayBuffer) {
        const gif = parseGIF(arrayBuffer);
        const frames = decompressFrames(gif, true);
        const { width, height } = frames[0].dims;
        const duration = frames.reduce((acc, frame) => acc + frame.delay, 0);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        let frameImageData;
        const tempCtx = createTempCanvas(width, height).getContext('2d');
        const framesData = [];
        for (let i = 0; i < frames.length; i++) {
            // console.log('frames[i].patch', frames[i].patch);
            // https://github.com/matt-way/gifuct-js/blob/master/demo/demo.js
            const frame = frames[i];
            const previousFrame = frames[i - 1];
            const dims = frame.dims;
            if (
                !frameImageData ||
                dims.width !== frameImageData.width ||
                dims.height !== frameImageData.height
            ) {
                tempCtx.canvas.width = dims.width;
                tempCtx.canvas.height = dims.height;
                frameImageData = tempCtx.createImageData(dims.width, dims.height);
            }
            // set the patch data as an override
            frameImageData.data.set(frame.patch);
            // draw the patch back over the canvas
            tempCtx.putImageData(frameImageData, 0, 0);
            // https://stackoverflow.com/questions/28056636/animated-gif-on-fabric-js-canvas/71664635#71664635
            if (previousFrame?.disposalType === 2) {
                const {width, height, left, top} = previousFrame.dims;
                ctx.clearRect(left, top, width, height);
            }
            ctx.drawImage(tempCtx.canvas, dims.left, dims.top);
            framesData.push({
                // dataUrl: canvas.toDataURL(),
                canvas: cloneCanvas(canvas),
                delay: frames[i].delay,
            });
            // document.body.appendChild(Object.assign(new Image(), {
            //     src: canvas.toDataURL()
            // }));
        }
        return {
            frames: framesData,
            duration,
        };
    }

    return gifParse;

};

/**
 * @param {RequestInfo | URL} req 
 * @param {number} timeout 
 */
export const fetchWithTimeout = (req, timeout) => {
    if (!timeout || !isFinite(timeout)) return fetch(req);
    return Promise.race([
        fetch(req),
        sleep(timeout).then(() => Promise.reject(new Error(`[fetchWithTimeout]: timeout ${req}`))),
    ]);
};


/**
 * @template T
 * @param {T[]} array 
 * @param {() => number} random
 */
export function shuffle(array, random = Math.random) {
    let m = array.length, t, i;
    // While there remain elements to shuffle…
    while (m) {
        // Pick a remaining element…
        i = Math.floor(random() * m--);
        // And swap it with the current element.
        t = array[m];
        array[m] = array[i];
        array[i] = t;
    }
    return array;
}

/**
 * @template T
 * @param {() => Promise<T>} fn 
 * @param {number} count 
 */
export const retryIfFailed = async (fn, count = 1) => {
    let error;
    for (let i = 0; i < count; i++) {
        try {
            const value = await fn();
            return value;
        } catch (err) {
            error = err;
        }
    }
    throw error;
};

export function getIsForeignObjectBugExistNoCache() {
    for (const { x, w } of [
        { x: 0, w: 100 },
        { x: 50, w: 50 },
        { x: 25, w: 75 },
    ]) {
        const iframe = document.createElement('iframe');
        iframe.src = 'about:blank';
        document.body.appendChild(iframe);
        iframe.width = '100';
        iframe.height = '100';
        iframe.setAttribute('style', `
        position: absolute;
        left: 0;
        top: 0;
        width: 100px;
        height: 100px;
        border: 0;
        opacity: 0;
        visibility: hidden;
        outline: solid 1px black;
        `);
        const win = iframe.contentWindow;
        const doc = win.document;
        const svgCode = `
        <svg viewBox="0 0 100 100" width="100" overflow="visible" style="
            position: absolute;
            top: ${x}px;
            left: ${x}px;
            width: ${w}px;
            height: ${w}px;
        ">
            <foreignObject x="0" y="0" width="100" height="100" overflow="visible" style="
                position: relative;
            ">
                <span style="
                    position: absolute;
                    left: ${50}px;
                    top: ${50}px;
                    width: ${50}px;
                    height: ${50}px;
                    background-color: red;
                    cursor: pointer;
                "></span>
            </foreignObject>
            <foreignObject x="0" y="0" width="100" height="100" overflow="visible" style="
                position: relative;
            ">
                <span style="
                    position: absolute;
                    left: ${0}px;
                    top: ${0}px;
                    width: ${50}px;
                    height: ${50}px;
                    background-color: red;
                    cursor: pointer;
                "></span>
            </foreignObject>
        </svg>
        `;
        const headCode = `
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1">
        `;
        doc.write(`<!DOCTYPE html><html><head>${headCode}</head><body style="
        overflow: hidden;
        margin: 0;
        ">${svgCode}</body></html>`);
        doc.close();
        const svg = doc.querySelector('svg');
        const [span, span2] = svg.querySelectorAll('span');
        const getIsCorrect = (/** @type {HTMLSpanElement} */span, dx = 0.5, dy = dx) => {
            const spanRect = span.getBoundingClientRect();
            const cx = spanRect.x + spanRect.width * dx;
            const cy = spanRect.y + spanRect.height * dy;
            const isCorrect = doc.elementsFromPoint(cx, cy).includes(span);
            return isCorrect;
        };
        for (const [dx, dy] of [
            [0.5, 0.5],
            [0.25, 0.25],
            [0.25, 0.75],
        ]) {
            const isCorrect = getIsCorrect(span, dx, dy);
            const isCorrect2 = getIsCorrect(span2, dx, dy);
            if (!isCorrect || !isCorrect2) {
                iframe.remove();
                return true;
            }
        }
        iframe.remove();
    }
    return false;
}

export const getIsForeignObjectBugExist = memoize(getIsForeignObjectBugExistNoCache);

export class LocalStorageMapItem {
    public storeKey: string;
    constructor(storeKey: string, version: number) {
        this.storeKey = storeKey + '_v' + version;
        this.clearOldVersions(storeKey, version);
    }
    clearOldVersions(storeKey: string, version: number) {
        try {
            const keys = [];
            let keyIndex = 0;
            while (true) {
                const key = localStorage.key(keyIndex++);
                if (typeof key !== 'string') break;
                keys.push(key);
            }
            for (const key of keys) {
                const keyVersionMatch = key.match(/_v(\d+)$/);
                if (!keyVersionMatch) continue;
                const keyPrefix = key.slice(0, -keyVersionMatch[0].length);
                if (keyPrefix !== storeKey) continue;
                const keyVersion = +keyVersionMatch[1];
                if (keyVersion >= version) continue;
                localStorage.removeItem(key);
            }
        } catch (err) {
            console.warn(err);
        }
    }
    /** @param {string} entryKey */
    has(entryKey) {
        try {
            const map = JSON.parse(localStorage.getItem(this.storeKey));
            return !!(map && (entryKey in map));
        } catch {
            return false;
        }
    }
    /** 
     * @param {string} entryKey
     * @param {any} value
     */
    set(entryKey, value) {
        try {
            let map;
            try {
                map = JSON.parse(localStorage.getItem(this.storeKey));
            } catch {}
            if (!map || typeof map !== 'object') map = {};
            map[entryKey] = value;
            localStorage.setItem(this.storeKey, JSON.stringify(map));
        } catch (err) {
            console.warn(err);
        }
    }
    /** @param {string} entryKey */
    get(entryKey) {
        try {
            const map = JSON.parse(localStorage.getItem(this.storeKey));
            if (!map) return undefined;
            return map[entryKey];
        } catch (err) {
            console.warn(err);
            return undefined;
        }
    }
}

if (typeof window !== 'undefined') Object.assign(window, { LocalStorageMapItem });
